/**
 * 指挥页直播状态 / 流地址工具（从 Dashboard.js 抽出）
 */
import streamingConfig from '../../config/streaming-config';

/** 流名规范：去空格，保留大小写 */
export function buildStreamKey(memberName) {
  return String(memberName || '').replace(/\s+/g, '');
}

/**
 * 队员已绑定的直播推流码（仅 stream_key / push_code / streamKey）。
 * 未填写 → null（未绑定）；不得回退到显示名——上线与直播解耦。
 */
export function getMemberBoundStreamKey(member) {
  const fromField = String(member?.stream_key || member?.streamKey || member?.push_code || '').trim();
  return fromField || null;
}

/**
 * 打开直播 / 拼地址时用的 stream_key。
 * 仅返回显式绑定值；无绑定返回空串（直播按钮也不应激活）。
 */
export function resolveMemberStreamKey(member) {
  return getMemberBoundStreamKey(member) || '';
}

export function buildStreamUrls(streamKey, config = streamingConfig) {
  const key = buildStreamKey(streamKey);
  return {
    hls: config.getHlsUrl(key),
    rtmp: config.getRtmpUrl(key),
    flv: config.getFlvUrl(key),
    webrtc: config.getWebRtcUrl(key),
    srt: config.getSrtUrl(key),
    streamKey: key
  };
}

/**
 * 查询某推流码是否正在直播（参数应为 stream_key，而非显示名）
 * @returns {Promise<boolean>}
 */
export async function checkStreamStatus(streamKeyOrName, options = {}) {
  const token = options.token != null ? options.token : localStorage.getItem('token');
  const key = buildStreamKey(streamKeyOrName);
  if (!key) return false;
  try {
    const response = await fetch(`/api/streaming/status/${encodeURIComponent(key)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    });

    if (!response.ok) {
      console.log(`推流码 ${key} 流媒体状态检查失败:`, response.status);
      return false;
    }

    const data = await response.json();
    if (data.success && data.isStreaming) {
      console.log(`推流码 ${key} 正在直播`);
      return true;
    }
    console.log(`推流码 ${key} 未在直播:`, data.message);
    return false;
  } catch (error) {
    console.log(`推流码 ${key} 流媒体检查异常:`, error);
    return false;
  }
}

/**
 * 批量检测：仅当队员已填写 stream_key，且该码在 SRS 上推流中，才激活「直播」按钮。
 * 无 stream_key → 未绑定，按钮保持灰色；Larix 仍可在多窗口直播独立显示。
 * Set 内存的是 member.name，供 MemberTreeList 按姓名匹配行。
 */
export async function checkMembersStreaming(members = []) {
  const streaming = new Set();
  for (const member of members) {
    if (!member?.name) continue;
    const boundKey = getMemberBoundStreamKey(member);
    if (!boundKey) continue;
    const ok = await checkStreamStatus(boundKey);
    if (ok) streaming.add(member.name);
  }
  return streaming;
}

/** 在 liveStreamVideo 容器中尝试嵌入 VLC 插件 */
export function loadEmbeddedVlcPlayer(options = {}) {
  const {
    videoElementId = 'liveStreamVideo',
    streamUrl = typeof window !== 'undefined' ? window.currentStreamUrls?.hls : null,
    onAlert = (msg) => alert(msg)
  } = options;

  const videoEl = document.getElementById(videoElementId);
  const videoContainer = videoEl?.parentElement;
  if (!videoContainer) {
    onAlert('找不到视频容器');
    return false;
  }
  if (!streamUrl) {
    onAlert('流媒体连接不可用');
    return false;
  }

  videoContainer.innerHTML = '';

  try {
    const vlcElement = document.createElement('object');
    vlcElement.setAttribute('type', 'application/x-vlc-plugin');
    vlcElement.setAttribute('pluginspage', 'http://www.videolan.org');
    vlcElement.setAttribute('width', '100%');
    vlcElement.setAttribute('height', '100%');
    vlcElement.setAttribute('id', 'embeddedVLC');
    vlcElement.style.borderRadius = '8px';

    [
      { name: 'mrl', value: streamUrl },
      { name: 'volume', value: '50' },
      { name: 'autoplay', value: 'true' },
      { name: 'loop', value: 'false' },
      { name: 'controls', value: 'true' }
    ].forEach((param) => {
      const paramElement = document.createElement('param');
      paramElement.setAttribute('name', param.name);
      paramElement.setAttribute('value', param.value);
      vlcElement.appendChild(paramElement);
    });

    videoContainer.appendChild(vlcElement);

    setTimeout(() => {
      try {
        const vlcPlugin = document.getElementById('embeddedVLC');
        if (vlcPlugin && vlcPlugin.playlist) {
          console.log('嵌入式VLC播放器加载成功');
          onAlert('嵌入式VLC播放器加载成功！');
        } else {
          throw new Error('VLC插件未正确初始化');
        }
      } catch (e) {
        console.error('VLC插件检测失败:', e);
        onAlert('VLC插件检测失败，请确保已安装VLC媒体播放器\n\n可选方案：\n1. 安装VLC播放器\n2. 使用外部播放器打开\n3. 继续使用HLS.js播放器');
        videoContainer.innerHTML = `
            <video
              id="${videoElementId}"
              controls
              autoPlay
              muted
              playsInline
              crossOrigin="anonymous"
              style="width:100%;height:100%;object-fit:contain;"
            >
              <source src="${streamUrl}" type="application/x-mpegURL" />
              您的浏览器不支持视频播放
            </video>
          `;
      }
    }, 3000);
    return true;
  } catch (error) {
    console.error('创建VLC播放器失败:', error);
    onAlert('创建VLC播放器失败: ' + error.message);
    return false;
  }
}